import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { GlobalContext } from '../../../store';
import { ISetClubId, setClubId } from './BoundActionCreators/setClubId';
import { ISetEventId, setEventId } from './BoundActionCreators/setEventId';
import { ISetRoundId, setRoundId } from './BoundActionCreators/setRoundId';

type IActions =
  | { type: 'setClubId'; value: string | undefined }
  | { type: 'setEventId'; value: string | undefined }
  | { type: 'setRoundId'; value: string | undefined };

interface IRouteInfoState {
  clubId?: string;
  eventId?: string;
  roundId?: string;
}

const initialState: IRouteInfoState = {};

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const RouteInfoStateContext = createContext<IRouteInfoState | undefined>(undefined);
const RouteInfoDispatchContext = createContext<IDispatch | undefined>(undefined);

const RouteInfoReducer = (state: IRouteInfoState, action: IActions): IRouteInfoState => {
  switch (action.type) {
    case 'setClubId': {
      return {
        ...state,
        clubId: action.value
      };
    }
    case 'setEventId': {
      return {
        ...state,
        eventId: action.value
      };
    }
    case 'setRoundId': {
      return {
        ...state,
        roundId: action.value
      };
    }
  }
};

interface TeamsProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  setClubId: ISetClubId;
  setEventId: ISetEventId;
  setRoundId: ISetRoundId;
}

export type IStateDispatchActions<T> = (
  state: IRouteInfoState,
  dispatch: (action: IActions) => void
) => T;

const RouteInfoProvider = ({ children }: TeamsProviderProps) => {
  const [state, dispatch] = useReducer(RouteInfoReducer, initialState as never);

  const {} = useContext(GlobalContext);

  const actions: IBoundActionCreators = {
    setClubId: setClubId(state, dispatch),
    setEventId: setEventId(state, dispatch),
    setRoundId: setRoundId(state, dispatch)
  };

  return (
    <RouteInfoStateContext.Provider value={state}>
      <RouteInfoDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </RouteInfoDispatchContext.Provider>
    </RouteInfoStateContext.Provider>
  );
};

const useRouteInfoState = () => {
  const context = React.useContext(RouteInfoStateContext);
  if (context === undefined) {
    throw new Error('useRouteInfoState must be used within a RouteInfoProvider');
  }
  return context;
};

const useRouteInfoDispatch = () => {
  const context = React.useContext(RouteInfoDispatchContext);
  if (context === undefined) {
    throw new Error('useRouteInfoDispatch must be used within a RouteInfoProvider');
  }
  return context;
};

export { RouteInfoProvider, useRouteInfoState, useRouteInfoDispatch };
